import { AdPlacement } from '@/features/ads/shared/types/ad-placement';
import { selectConsentFramework } from '@/features/consent/shared/state/selectors';
import {
  cleanupSlots,
  impressionViewableHandler,
  initGPTPlacements,
  refreshAdUnits,
} from '@/features/ads/internal/helper/gpt-ads-helper';
import {
  Pbjs,
  removePrebidAdUnits,
  requestPrebidBids,
  setPrebidAdUnits,
  setPrebidConfig,
} from '@/features/ads/internal/helper/prebid-helper';

interface InitializePlacementsArgs {
  placements: AdPlacement[];
  pbjs?: Pbjs;
  googletag: typeof window.googletag;
  consentFramework: ReturnType<typeof selectConsentFramework>;
  setTimeoutForSlot: (key: string, callback: () => void, delay: number) => void;
  clearAll: () => void;
}

export const initializePlacements = ({
  placements,
  pbjs,
  googletag,
  consentFramework,
  setTimeoutForSlot,
  clearAll,
}: InitializePlacementsArgs) => {
  initGPTPlacements(googletag, placements);

  const { refresh: refreshPlacements, cleanup: cleanupPrebid } = (() => {
    if (!pbjs) {
      return {
        refresh: (slot?: googletag.Slot[]) => refreshAdUnits(googletag, slot),
        cleanup: () => {},
      };
    }

    setPrebidConfig(pbjs, consentFramework);
    const prebidAdUnits = placements.map((placement) => placement.prebidAdUnit);
    const prebidAdUnitCodes = prebidAdUnits.map((unit) => unit.code);
    setPrebidAdUnits(pbjs, prebidAdUnits);

    return {
      refresh: (slot?: googletag.Slot[], adUnitCodes?: string[]) => {
        requestPrebidBids(pbjs, () => refreshAdUnits(googletag, slot), adUnitCodes);
      },
      cleanup: () => {
        removePrebidAdUnits(pbjs, prebidAdUnitCodes);
      },
    };
  })();

  const cleanUpImpressionViewableHandler = impressionViewableHandler(googletag, (event) => {
    const slot = event.slot;
    const slotId = slot.getSlotElementId();
    const shouldRefresh = slot.getTargeting('shouldRefresh').includes('true');
    const placement = placements.find((p) => p.code === slotId);
    const delay = placement?.refreshTimeInSeconds ?? 30;

    if (shouldRefresh && placement) {
      setTimeoutForSlot(
        slotId,
        () => {
          refreshPlacements([slot], [slotId]);
        },
        delay * 1000,
      );
    }
  });

  refreshPlacements();

  return () => {
    cleanupPrebid();
    cleanupSlots(googletag);
    cleanUpImpressionViewableHandler();
    clearAll();
  };
};
